import type { SidebarGroup, SidebarItem } from '../services/types';
import { Sidebar } from './Sidebar';
import { UserMenu } from '../widgets/UserMenu';
import { Sheet, SheetContent, SheetTitle } from '../ui/sheet';

interface MobileDrawerProps {
  open: boolean;
  onClose: () => void;
  sidebarItems?: SidebarItem[];
  sidebarGroups?: SidebarGroup[];
  activeSidebarPath?: string;
  appName: string;
}

/**
 * Full-height left drawer for small screens — shows the current subsystem's
 * sidebar only. Service switching is handled by the TopBar dropdown
 * (visible on all screen sizes), so the drawer doesn't duplicate it.
 *
 * Built on shadcn Sheet (which wraps Radix Dialog). Focus trap, scroll
 * lock, escape, backdrop click, and the close button are all handled.
 */
export function MobileDrawer({
  open,
  onClose,
  sidebarItems,
  sidebarGroups,
  activeSidebarPath,
  appName,
}: MobileDrawerProps) {
  const hasSidebar = Boolean(sidebarItems?.length || sidebarGroups?.length);

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent
        side="left"
        aria-describedby={undefined}
        className="lg:hidden flex flex-col w-72 max-w-[85vw] p-0 gap-0 sm:max-w-[85vw]"
      >
        {/* Fixed header */}
        <div className="shrink-0 flex items-center h-14 px-4 border-b">
          <SheetTitle className="text-sm font-semibold">{appName}</SheetTitle>
        </div>

        {/* Scrollable middle: subsystem sidebar only. */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          {hasSidebar ? (
            <Sidebar
              items={sidebarItems}
              groups={sidebarGroups}
              activePath={activeSidebarPath}
              onNavigate={onClose}
            />
          ) : (
            <div className="px-4 py-6 text-xs text-muted-foreground">No menu items.</div>
          )}
        </div>

        {/* Pinned footer with user menu (mirrors desktop sidebar footer) */}
        <div className="shrink-0 border-t px-3 py-2">
          <UserMenu placement="top" />
        </div>
      </SheetContent>
    </Sheet>
  );
}
